import { getRollbar } from '../../modules/analytics/rollbar';
import { buildPostLoginUrl, hasForceLogin } from '../../modules/auth/utils/verifyOAuthParams';
import { AuthState, type StateActionArguments } from './types';

export const mapOfAuthStateToAction: Record<AuthState, (args: StateActionArguments) => Promise<void> | void> = {
  // Skip signing in and permission check when running for tests
  // Go to checking permissions immediately if we have user data
  [AuthState.UNAUTHENTICATED]: async ({ firebaseListener, setAuthState, skipAuth, location, pageIsPublic }) => {
    if (skipAuth) {
      setAuthState(AuthState.PERMISSIONS_CHECKED);
      return;
    }

    if (firebaseListener.loading) {
      return;
    }

    if (firebaseListener.user) {
      const isOnLoginPage = location.pathname.includes('login');
      const shouldRedirect = isOnLoginPage && !hasForceLogin(location);

      if (shouldRedirect) {
        let token: string | null = null;

        try {
          token = await firebaseListener.user.getIdToken(false);
        } catch (error) {
          getRollbar()?.error('[Auth] Failed to retrieve ID token, redirecting without token:', error);
        }

        window.location.replace(buildPostLoginUrl(location, token));
        return;
      }

      setAuthState(AuthState.AUTHENTICATED);
      return;
    }

    if (!pageIsPublic) {
      // changes to window.location cause refetch of page by browser, which will recreate apollo client(and clear cache)
      window.location.replace(`/login${location.search}${location.hash}`);
    }
  },
  [AuthState.AUTHENTICATED]: ({
    checkPermissionQuery,
    firebaseListener,
    setAuthState,
    triggerCheckPermissionsQuery,
  }) => {
    if (!firebaseListener.user) {
      return;
    }

    if (!checkPermissionQuery.called) {
      triggerCheckPermissionsQuery({ variables: { uid: firebaseListener.user.uid } });
    } else {
      checkPermissionQuery.refetch?.({ uid: firebaseListener.user.uid });
    }
    setAuthState(AuthState.CHECKING_PERMISSIONS);
  },

  // If permission check data arrived refetch token if needed
  [AuthState.CHECKING_PERMISSIONS]: async ({ checkPermissionQuery, fetchAuthToken, setAuthState, auth }) => {
    if (!checkPermissionQuery.data) return;

    const { checkUserPermissions } = checkPermissionQuery.data;

    if (checkUserPermissions?.forceRefreshToken) {
      // Only happened to users which don't have customClaims
      // Which means that account was created before proper permissions model
      await fetchAuthToken({ auth, forceRefresh: true });
    }

    setAuthState(AuthState.PERMISSIONS_CHECKED);
  },

  // Trigger query for session for the first time or again in case of reloading session
  [AuthState.PERMISSIONS_CHECKED]: ({ sessionQuery, setAuthState, triggerSessionQuery }) => {
    if (!sessionQuery.called) {
      triggerSessionQuery();
    } else {
      sessionQuery.refetch?.();
    }
    setAuthState(AuthState.OBTAINING_SESSION);
  },

  // If session data arrived go next state
  [AuthState.OBTAINING_SESSION]: ({ sessionQuery, setAuthState }) => {
    if (sessionQuery.loading) return;
    setAuthState(AuthState.SESSION_OBTAINED);
  },

  // If terms are not accepted and user is on private page then redirect to terms acceptance
  [AuthState.SESSION_OBTAINED]: ({
    navigate,
    pageIsPublic,
    skipAuth,
    sessionContextValue,
    firebaseListener,
    setAuthState,
    location,
    logout,
  }) => {
    //Logout functionality
    if (!firebaseListener.user && !skipAuth) {
      setAuthState(AuthState.UNAUTHENTICATED);
      return;
    }

    if (hasForceLogin(location)) {
      logout();
      return;
    }

    if (pageIsPublic || sessionContextValue?.terms_and_conditions_accepted || location.pathname === '/terms-acceptance')
      return;

    navigate('/terms-acceptance');
  },

  // If session reload was requested (by changing to SESSION_STALE) than wait for session query to start refetching
  [AuthState.SESSION_STALE]: ({ sessionQuery, setAuthState }) => {
    if (!sessionQuery.loading) return;
    setAuthState(AuthState.OBTAINING_SESSION);
  },
};
